import apiClient from './client';
import type {
  AttributionBreakdown,
  AttributionBreakdownQuery,
  CreateSourceChannelRuleDto,
  CreateYandexAppDto,
  DirectAnalyticsQuery,
  DirectAnalyticsReport,
  SetYandexCampaignBranchDto,
  SourceChannelRulesResponse,
  SourcesAnalyticsQuery,
  SourcesAnalyticsReport,
  SourcesNodeLeadsQuery,
  UtmSourceChannelRuleDto,
  MetrikaCounterView,
  RunYandexSyncDto,
  StartYandexConnectionDto,
  UpdateMetrikaCounterDto,
  UpdateYandexAppDto,
  UpdateYandexConnectionDto,
  UpdateYandexDirectAccountDto,
  UpdateYandexSettingsDto,
  YandexAppView,
  YandexAuthSessionView,
  YandexCampaignChildrenQuery,
  YandexCampaignChildrenResponse,
  YandexCampaignView,
  YandexConnectionView,
  YandexDirectAccountView,
  YandexIntegrationView,
  YandexSettingsView,
  YandexNodeLeadsQuery,
  YandexNodeLeadsResponse,
  YandexSyncJobView,
  YandexSyncStatusView,
} from '@ads-manager/shared';

const BASE_URL = '/yandex';

/**
 * Подключения заказчика (без канала подрядчика) запрашиваются меткой `own`:
 * пустой query-параметр не отличить от отсутствующего.
 */
export const OWN_ACCOUNTS_SCOPE = 'own';

/** Директ формирует отчёт офлайн — минуты ожидания здесь норма, а не сбой. */
const ANALYTICS_TIMEOUT_MS = 180_000;

// --- Очередь задач выкачки --------------------------------------------------

/** Что выкачивается сейчас и когда данные выкачивались в последний раз. */
export const getYandexSyncStatus = async (): Promise<YandexSyncStatusView> => {
  const response = await apiClient.get<YandexSyncStatusView>(
    `${BASE_URL}/sync/status`
  );
  return response.data;
};

/** Поставить задачу выкачки; такая же задача в очереди не дублируется. */
export const runYandexSync = async (
  dto: RunYandexSyncDto
): Promise<YandexSyncJobView> => {
  const response = await apiClient.post<YandexSyncJobView>(
    `${BASE_URL}/sync/run`,
    dto
  );
  return response.data;
};

/** Первичная загрузка за initialLoadDays (только трафик-менеджер). */
export const startYandexBackfill = async (): Promise<YandexSyncJobView> => {
  const response = await apiClient.post<YandexSyncJobView>(
    `${BASE_URL}/sync/backfill`
  );
  return response.data;
};

export const getYandexSettings = async (): Promise<YandexSettingsView> => {
  const response = await apiClient.get<YandexSettingsView>(
    `${BASE_URL}/settings`
  );
  return response.data;
};

export const updateYandexSettings = async (
  dto: UpdateYandexSettingsDto
): Promise<YandexSettingsView> => {
  const response = await apiClient.put<YandexSettingsView>(
    `${BASE_URL}/settings`,
    dto
  );
  return response.data;
};

/** Привязка + каналы, по которым текущему пользователю можно подключать аккаунты. */
export const getYandexIntegration = async (): Promise<YandexIntegrationView> => {
  const response = await apiClient.get<YandexIntegrationView>(
    `${BASE_URL}/integration`
  );
  return response.data;
};

export const getYandexApps = async (): Promise<YandexAppView[]> => {
  const response = await apiClient.get<YandexAppView[]>(`${BASE_URL}/apps`);
  return response.data;
};

export const createYandexApp = async (
  dto: CreateYandexAppDto
): Promise<YandexAppView> => {
  const response = await apiClient.post<YandexAppView>(`${BASE_URL}/apps`, dto);
  return response.data;
};

export const updateYandexApp = async (
  id: string,
  dto: UpdateYandexAppDto
): Promise<YandexAppView> => {
  const response = await apiClient.patch<YandexAppView>(
    `${BASE_URL}/apps/${id}`,
    dto
  );
  return response.data;
};

export const deleteYandexApp = async (id: string): Promise<{ id: string }> => {
  const response = await apiClient.delete<{ id: string }>(
    `${BASE_URL}/apps/${id}`
  );
  return response.data;
};

export const getYandexConnections = async (
  contractorChannelId?: string
): Promise<YandexConnectionView[]> => {
  const response = await apiClient.get<YandexConnectionView[]>(
    `${BASE_URL}/connections`,
    contractorChannelId ? { params: { contractorChannelId } } : undefined
  );
  return response.data;
};

export const startYandexConnection = async (
  dto: StartYandexConnectionDto
): Promise<YandexAuthSessionView> => {
  const response = await apiClient.post<YandexAuthSessionView>(
    `${BASE_URL}/connections/start`,
    dto
  );
  return response.data;
};

export const getYandexAuthSession = async (
  sessionId: string
): Promise<YandexAuthSessionView> => {
  const response = await apiClient.get<YandexAuthSessionView>(
    `${BASE_URL}/connections/sessions/${sessionId}`
  );
  return response.data;
};

export const verifyYandexConnection = async (
  id: string
): Promise<YandexConnectionView> => {
  const response = await apiClient.post<YandexConnectionView>(
    `${BASE_URL}/connections/${id}/verify`
  );
  return response.data;
};

export const reauthYandexConnection = async (
  id: string
): Promise<YandexAuthSessionView> => {
  const response = await apiClient.post<YandexAuthSessionView>(
    `${BASE_URL}/connections/${id}/reauth`
  );
  return response.data;
};

export const updateYandexConnection = async (
  id: string,
  dto: UpdateYandexConnectionDto
): Promise<YandexConnectionView> => {
  const response = await apiClient.patch<YandexConnectionView>(
    `${BASE_URL}/connections/${id}`,
    dto
  );
  return response.data;
};

export const deleteYandexConnection = async (
  id: string
): Promise<{ id: string }> => {
  const response = await apiClient.delete<{ id: string }>(
    `${BASE_URL}/connections/${id}`
  );
  return response.data;
};

export const updateYandexDirectAccount = async (
  id: string,
  dto: UpdateYandexDirectAccountDto
): Promise<YandexDirectAccountView> => {
  const response = await apiClient.patch<YandexDirectAccountView>(
    `${BASE_URL}/direct-accounts/${id}`,
    dto
  );
  return response.data;
};

export const updateMetrikaCounter = async (
  id: string,
  dto: UpdateMetrikaCounterDto
): Promise<MetrikaCounterView> => {
  const response = await apiClient.patch<MetrikaCounterView>(
    `${BASE_URL}/metrika-counters/${id}`,
    dto
  );
  return response.data;
};

/**
 * Сводная аналитика по каналу. Отчёт собирается на живых данных Директа
 * и Метрики, поэтому первый запрос за период занимает секунды — таймаут
 * клиента для него поднят отдельно.
 */
export const getDirectAnalytics = async (
  query: DirectAnalyticsQuery
): Promise<DirectAnalyticsReport> => {
  const response = await apiClient.get<DirectAnalyticsReport>(
    `${BASE_URL}/analytics/direct`,
    { params: query, timeout: ANALYTICS_TIMEOUT_MS }
  );
  return response.data;
};

/**
 * Заявки за одним числом колонки «Заявки», «Квалы» или «Продажи» — то, что
 * открывается кликом по нему. Параметры те же, что у отчёта: список обязан
 * сойтись с числом, из которого открыт, а для этого он должен считаться на
 * том же периоде и разрезе.
 */
export const getDirectNodeLeads = async (
  query: YandexNodeLeadsQuery
): Promise<YandexNodeLeadsResponse> => {
  const response = await apiClient.get<YandexNodeLeadsResponse>(
    `${BASE_URL}/analytics/direct/leads`,
    { params: query, timeout: ANALYTICS_TIMEOUT_MS }
  );
  return response.data;
};

/**
 * Следующие строки детализации одной кампании — «Показать ещё» под её срезом.
 * Параметры — те же, что у отчёта: продолжение должно состыковаться с первыми
 * строками, а для этого считаться на том же периоде, разрезе и сравнении.
 */
export const getDirectCampaignChildren = async (
  query: YandexCampaignChildrenQuery
): Promise<YandexCampaignChildrenResponse> => {
  const response = await apiClient.get<YandexCampaignChildrenResponse>(
    `${BASE_URL}/analytics/direct/children`,
    { params: query, timeout: ANALYTICS_TIMEOUT_MS }
  );
  return response.data;
};

/**
 * Тот же отчёт книгой Excel. Параметры — тот же объект, что и у таблицы:
 * второе место, где они перечислены руками, рано или поздно разъедется
 * с первым, и человек получит файл про другой период, не заметив этого.
 */
export const downloadDirectAnalytics = async (
  query: DirectAnalyticsQuery
): Promise<{ blob: Blob; fileName: string }> => {
  const { compare: _compare, refresh: _refresh, ...params } = query;
  const response = await apiClient.get<Blob>(
    `${BASE_URL}/analytics/direct/export`,
    { params, responseType: 'blob', timeout: ANALYTICS_TIMEOUT_MS }
  );

  const disposition = String(
    response.headers['content-disposition'] ?? ''
  );
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: response.data,
    fileName: match?.[1] ?? `direct-${params.dateFrom}_${params.dateTo}.xlsx`,
  };
};

/** Разбор качества привязки: где теряются заявки. Только трафик-менеджеру. */
export const getAttributionBreakdown = async (
  query: AttributionBreakdownQuery
): Promise<AttributionBreakdown> => {
  const response = await apiClient.get<AttributionBreakdown>(
    `${BASE_URL}/attribution/breakdown`,
    { params: query }
  );
  return response.data;
};

// --- «Все источники» (этапы 10.5–10.7)

/** Все источники визитов и заявок за период — только трафик-менеджеру. */
export const getSourcesAnalytics = async (
  query: SourcesAnalyticsQuery
): Promise<SourcesAnalyticsReport> => {
  const response = await apiClient.get<SourcesAnalyticsReport>(
    `${BASE_URL}/analytics/sources`,
    { params: query, timeout: ANALYTICS_TIMEOUT_MS }
  );
  return response.data;
};

/**
 * Заявки за одним числом колонки «Заявки», «Квалы» или «Продажи» отчёта
 * «Все источники».
 */
export const getSourcesNodeLeads = async (
  query: SourcesNodeLeadsQuery
): Promise<YandexNodeLeadsResponse> => {
  const response = await apiClient.get<YandexNodeLeadsResponse>(
    `${BASE_URL}/analytics/sources/leads`,
    { params: query, timeout: ANALYTICS_TIMEOUT_MS }
  );
  return response.data;
};

/** Тот же отчёт книгой Excel — за выбранный период, без сравнения. */
export const downloadSourcesAnalytics = async (
  query: SourcesAnalyticsQuery
): Promise<{ blob: Blob; fileName: string }> => {
  const { compare: _compare, refresh: _refresh, ...params } = query;
  const response = await apiClient.get<Blob>(
    `${BASE_URL}/analytics/sources/export`,
    { params, responseType: 'blob', timeout: ANALYTICS_TIMEOUT_MS }
  );
  const disposition = String(
    response.headers['content-disposition'] ?? ''
  );
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: response.data,
    fileName: match?.[1] ?? `sources-${params.dateFrom}_${params.dateTo}.xlsx`,
  };
};

/** Сопоставления «utm_source → канал подрядчика» и каналы для выбора. */
export const getSourceChannelRules =
  async (): Promise<SourceChannelRulesResponse> => {
    const response = await apiClient.get<SourceChannelRulesResponse>(
      `${BASE_URL}/analytics/sources/rules`
    );
    return response.data;
  };

export const createSourceChannelRule = async (
  dto: CreateSourceChannelRuleDto
): Promise<UtmSourceChannelRuleDto> => {
  const response = await apiClient.post<UtmSourceChannelRuleDto>(
    `${BASE_URL}/analytics/sources/rules`,
    dto
  );
  return response.data;
};

export const deleteSourceChannelRule = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/analytics/sources/rules/${id}`);
};

// --- кампании Директа и их филиал

/**
 * Ручной филиал кампании без заявок; `branchId: null` — снять. Кампании
 * с заявками бэкенд откажет: их филиал выводится из заявок.
 */
export const setYandexCampaignBranch = async (
  id: string,
  dto: SetYandexCampaignBranchDto
): Promise<YandexCampaignView> => {
  const response = await apiClient.patch<YandexCampaignView>(
    `${BASE_URL}/campaigns/${id}/branch`,
    dto
  );
  return response.data;
};

export const yandexApi = {
  getYandexSettings,
  updateYandexSettings,
  getYandexIntegration,
  getYandexApps,
  createYandexApp,
  updateYandexApp,
  deleteYandexApp,
  getYandexConnections,
  startYandexConnection,
  getYandexAuthSession,
  verifyYandexConnection,
  reauthYandexConnection,
  updateYandexConnection,
  deleteYandexConnection,
  updateYandexDirectAccount,
  updateMetrikaCounter,
  getDirectAnalytics,
  getDirectNodeLeads,
  getDirectCampaignChildren,
  downloadDirectAnalytics,
  getAttributionBreakdown,
  getSourcesAnalytics,
  getSourcesNodeLeads,
  downloadSourcesAnalytics,
  getSourceChannelRules,
  createSourceChannelRule,
  deleteSourceChannelRule,
  setYandexCampaignBranch,
  getYandexSyncStatus,
  runYandexSync,
  startYandexBackfill,
};

export default yandexApi;
